import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {injectIntl, FormattedMessage} from 'react-intl';
import {connect} from "react-redux";
import Form from "react-jsonschema-form";

import apiCalls from '../utils/apiCalls';
import utils from '../utils/utils';
import ImagesForm from "../components/imagesForm";
import AutoResizeTextarea from "../components/customFields/autoResizeTextarea";
import CustomSelect from "../components/customFields/customSelect";
import RichText from "../components/customFields/richText";
import ItemMarketplaces from "../components/customFields/itemMarketplaces";
import classNames from "classnames";

const MarketplaceAdminItemBulkForm = ({currentUser, intl, history, marketplace}) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkItems, setBulkItems] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);

  useEffect(() => {
    setLoading(true);
    apiCalls.getMarketplaces({
      userId: currentUser.id,
    })
      .then((result) => {
        if (result.data && result.data.marketplaces) {
          setMarketplaces(result.data.marketplaces);
          setLoading(false);
        }
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
        setLoading(false);
      });
  }, []);

  const onImagesChange = (newImages) => {
    const newItems = bulkItems.filter((item) => newImages.some((image) => image.id === item.image.id));

    newImages.forEach((image) => {
      if (!newItems.some((item) => item.image.id === image.id)) {
        newItems.push(
          {
            image: image,
            item: {
              marketplaces: [{marketplaceId: marketplace.id}],
            },
          }
        );
      }
    });

    setBulkItems(newItems);
  };

  const onItemChange = (newItem, index) => {
    const newItems = bulkItems;
    newItems[index].item = newItem;

    setBulkItems(newItems);
  };

  const onSubmit = () => {
    setLoading(true);

    apiCalls.createItem({
      items: bulkItems.map(item => {
        return {
          item: item.item,
          images: [item.image]
        }
      })
    })
      .then((result) => {
        history.push({
          pathname: `/marketplaceAdmin/${marketplace.id}/view`
        });
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
        setLoading(false);
      });
  };

  const itemFormSchema = {
    type: "object",
    required: [],
    properties: {
      name: {
        type: "string",
        title: intl.formatMessage({id: 'item.form.field.name.label'})
      },
      description: {
        type: "string",
        title: intl.formatMessage({id: 'item.form.field.description.label'}),
        default: ''
      },
      price: {
        type: "string",
        title: intl.formatMessage({id: 'item.form.field.price.label'}),
        default: ''
      },
      marketplaces: {
        type: "array",
        title: intl.formatMessage({id: 'item.form.field.marketplaces.label'}),
        marketplaces: marketplaces,
      },
    }
  };

  const itemUiSchema = {
    price: {
      "ui:help": intl.formatMessage({id: 'item.form.field.price.help'}),
    },
    description: {
      "ui:field": "richText",
    },
    marketplaces: {
      "ui:field": "itemMarketplaces",
    },
  };

  const formFields = {
    autoResizeTextarea: AutoResizeTextarea,
    customSelect: CustomSelect,
    richText: RichText,
    itemMarketplaces: ItemMarketplaces,
  };

  const images = bulkItems.map((item) => item.image);

  const buttonClasses = classNames({
    "btn-loading": loading,
    "btn btn-primary": true
  });

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 marketplace-admin__form m-full-width">

        <h2 className="text-center m-b-6">
          <FormattedMessage id={'item.bulk.create.header'}/>
        </h2>

        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            {loading ? (
              <div className="text-center m-b-5">
                <div className="b-loader"/>
              </div>
            ) : ''}

            {error ? (
              <div className="alert alert-error m-b-5">
                {error}
              </div>
            ) : ''}

            <h3>
              <FormattedMessage id={'item.bulk.create.step.1.header'}/>
            </h3>
            <p>
              <FormattedMessage id={'item.bulk.create.step.1.description'}/>
            </p>
            <div className="m-b-6">
              <ImagesForm
                images={images}
                onImagesChange={onImagesChange}
                uploadImage={(image) => apiCalls.uploadImage(image, 'item')}
              />
            </div>
            <h3>
              <FormattedMessage id={'item.bulk.create.step.2.header'}/>
            </h3>
            <p>
              <FormattedMessage id={'item.bulk.create.step.2.description'}/>
            </p>

            <div className="row m-b-1">
              {
                bulkItems.map((item, index) => {
                  return (
                    <div key={index} className="col-12 col-md-6 m-b-2">
                      <div className="item-bulk-form__item">
                        <div className="item-bulk-form__item-image m-b-3">
                          <img src={item.image.thumbnailImage} alt=""/>
                        </div>
                        <Form schema={itemFormSchema} uiSchema={itemUiSchema} fields={formFields} showErrorList={false}
                              formData={item.item} onChange={(event) => onItemChange(event.formData, index)}
                              noValidate={true}>
                          <span/>
                        </Form>
                      </div>
                    </div>
                  );
                })
              }
            </div>

            <div className="text-center m-b-6">
              <button type="submit" className={buttonClasses} onClick={onSubmit} disabled={loading}>
                <FormattedMessage id="item.bulk.create.cta"/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MarketplaceAdminItemBulkForm.propTypes = {
  currentUser: PropTypes.object,
};

MarketplaceAdminItemBulkForm.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
    marketplace: state.marketplaces.marketplace,
  };
}

export default injectIntl(connect(mapStateToProps)(MarketplaceAdminItemBulkForm));